import React from 'react';
import { useEffectOnce } from 'react-use';
import { useTranslation } from 'react-i18next';

import { CG } from 'contexts';
import { useApi } from 'hooks';
import { RHP } from 'providers';
import { queryParam } from 'utils/query';

export function useRankingStartup() {
    const { t } = useTranslation('ranking');
    const { emitAlert } = React.useContext(CG);
    const api = useApi();

    // Initial values for the state
    const initial = {
        ranking: {
            id: '2021-CO',
            name: '2021-CO',
            year: '2021', // quando mudar o ano do ranking, mudar também as datas em calculateContractPoints
            country: 'CO',
        },
        application: { applied: false, steps: {} },
        startups: [],
        startupDetails: {},
        contracts: [],
        searchedCompany: {},
        contractPoints: 0,
        startupId: queryParam('startupId') || 0,
        startupTerm: {},
        term: {},
        redirectTo: '',
    };

    // Controls if the module is ready
    const [ready, setReady] = React.useState(false);

    // Define variables holder to be used on the module
    const [vars, setVars] = React.useState(initial);
    const addVars = newVars => setVars(old => ({ ...old, ...newVars }));

    /**
     * CUSTOM FUNCTIONS
     * ------------------------------------------------------------------------
     */

    const type2Points = React.useMemo(
        () => ({
            none: 0,
            match: 1,
            mentoring: 1,
            connection: 1,
            award: 1,
            coworking: 1,
            voucher: 5,
            outlicensing: 5,
            resources: 5,
            employee: 5,
            channels: 5,
            'r&d': 10,
            inlicensing: 10,
            pilot: 10,
            supplying: 10,
            acceleration: 10,
            investment: 10,
            accelerationLarge: 10,
            investmentLarge: 10,
            acquisition: 10,
        }),
        []
    );

    const calculateContractPoints = React.useCallback(
        contract => {
            const startDate = new Date(contract.startDate);
            let endDate = new Date(contract.endDate);

            if (contract.startDate && !contract.endDate) {
                endDate = new Date(contract.startDate);
                endDate.setFullYear(startDate.getFullYear() + 1);
            }

            // Check the dates
            if (
                endDate >= startDate &&
                endDate >= new Date('2019-05-01') &&
                startDate < new Date('2021-07-01')
            ) {
                if (contract.formalized !== 'true') {
                    addVars({ contractPoints: 1 });
                } else if (
                    contract.valueRange &&
                    Array.isArray(contract.valueRange) &&
                    contract.valueRange[0] >= 500000
                ) {
                    addVars({ contractPoints: 20 });
                } else {
                    addVars({ contractPoints: type2Points[contract.type] || 1 });
                }
            } else {
                addVars({ contractPoints: 0 });
            }
        },
        [type2Points]
    );

    function setReturnTo(href) {
        addVars({ redirectTo: href });
    }

    // //////////////////////
    //  RANKING FUNCTIONS  //
    // //////////////////////

    function getRanking(rankingId = initial.ranking.id) {
        return api.get(`/rankings`).then(res => {
            const filtered = res.filter(r => r.id === rankingId);
            addVars({ ranking: filtered[0] });
            return filtered[0];
        });
    }

    // ///////////////////
    //  TERM FUNCTIONS  //
    // ///////////////////

    function getTerm(term) {
        return api.get(`/terms/${term}`).then(res => {
            addVars({ term: res });
            return res;
        });
    }

    function getStartupTermByType(type, addResponseToVars = true) {
        return api.get(`/startups/${vars.startupId}/terms/${type}`).then(res => {
            if (addResponseToVars) {
                addVars({ startupTerm: res });
            }
            return res;
        });
    }

    function postStartupTerm(payload) {
        // payload = { termId, signed: true }
        return api.post(`/startups/${vars.startupId}/terms/`, payload).then(res => {
            addVars({ startupTerm: res });
            return res;
        });
    }

    // //////////////////////
    //  STARTUP FUNCTIONS  //
    // //////////////////////

    function getStartupDetails(startupId, addResponseToVars = true) {
        return api.get(`/startups/${startupId}`).then(res => {
            if (res.startupStatus === 'none') {
                RHP.push('/startup');
            }

            if (addResponseToVars) {
                addVars({ startupDetails: res });
            }
            return res;
        });
    }

    function getStartups() {
        return api.get(`/startups`).then(async res => {
            addVars({ startups: res });

            if (res.length === 1) {
                const startup = await getStartupDetails(res[0]._id);
                const country = startup.location.country.id;

                if (country === 'CO') {
                    addVars({ startupId: startup._id });
                } else if (country === 'BR') {
                    emitAlert(
                        t('rankingRedirection', { rankingCountry: vars.ranking.country, country })
                    );
                    RHP.push('/ranking/br');
                } else {
                    emitAlert(t('noRanking', { country }));
                }
            }

            return res;
        });
    }

    function putStartupDetails(payload) {
        return api.put(`/startups/${vars.startupId}`, payload).then(res => {
            return getStartupDetails(vars.startupId);
        });
    }

    // /////////////////////////////////
    // RANKING APPLICATION FUNCTIONS  //
    // /////////////////////////////////

    /**
     * CUSTOM FUNCTIONS
     * ------------------------------------------------------------------------
     */

    function getStartupRankingApplication(addResponseToVars = true) {
        return api
            .get(`/startups/${vars.startupId}/rankingapplications/${vars.ranking.id}`)
            .then(res => {
                // console.log('useRankingInvestor', 'getUserRankingApplication', res);
                let application = res;
                if (!res.steps) {
                    application = initial.application;
                }
                if (addResponseToVars) {
                    addVars({ application });
                }
                return application;
            });
    }
    function putStartupRankingApplication(payload, updateVars = true) {
        return api
            .put(`/startups/${vars.startupId}/rankingapplications/${vars.ranking.id}`, payload)
            .then(res => {
                getStartupDetails(vars.startupId);
                if (updateVars) addVars({ application: res });
                return res;
            });
    }

    /**
     * INCLUDE VARS FUNCTIONS
     * ------------------------------------------------------------------------
     */

    const updateSteps = update => {
        // Get the current application variable
        const { application } = vars;
        application.steps = { ...application.steps, ...update };
        addVars({ application });
        return putStartupRankingApplication({ ...application }, false);
    };

    // ///////////////////////
    // CONTRACT FUNCTIONS   //
    // ///////////////////////

    /**
     * CUSTOM FUNCTIONS
     * ------------------------------------------------------------------------
     */

    function getCompanyByDomain(companyDomain) {
        return api.get(`/companydomain/${companyDomain}`).then(res => {
            addVars({ searchedCompany: res });
            return res;
        });
    }

    const handleStartupContracts = res => {
        if (!res) return [];

        return res.map(contract => {
            const { ranking } = vars;

            // Se estiver fora do range do Ranking
            if (ranking.end && ranking.start) {
                const rankingEnd = new Date(ranking.end);
                const rankingStart = new Date(ranking.start);
                const contractEnd = new Date(contract.endDate);
                const contractStart = new Date(contract.startDate);
                const contractRegDate = new Date(contract.regDate);

                if (contractStart >= rankingEnd || contractEnd < rankingStart) {
                    contract.invalid = true;
                    contract.outOfRange = true;
                }
                // Se estiver cadastrado a mais de um ano
                const yearAgo = new Date();
                yearAgo.setFullYear(rankingStart.getFullYear() - 1);
                console.log(contract._id, contractRegDate, yearAgo);
                if (contractRegDate < yearAgo) {
                    contract.invalid = true;
                    contract.expired = true;
                }
            }
            return contract;
        });
    };

    function getStartupContracts(addResponseToVars = true) {
        return api
            .get(`/startups/${vars.startupId}/contracts`)
            .then(handleStartupContracts)
            .then(res => {
                if (addResponseToVars) {
                    addVars({ contracts: [...res] });
                }
                return [...res];
            });
    }

    function postStartupContract(payload) {
        return api.post(`/startups/${vars.startupId}/contracts`, payload).then(res => {
            return getStartupContracts();
        });
    }

    function deleteStartupContract(payload) {
        const { _id } = payload;
        return api.delete(`/startups/${vars.startupId}/contracts/${_id}`).then(res => {
            return getStartupContracts();
        });
    }

    function postStartupContractValidationEmail(payload) {
        const { _id } = payload;
        return api.post(`/startups/${vars.startupId}/contracts/${_id}/sendemail`).then(res => {
            return getStartupContracts();
        });
    }

    function postStartupContractReconsideration(payload) {
        const { _id } = payload;
        return api.post(`/startups/${vars.startupId}/contracts/${_id}/reconsideration`).then(() => {
            return getStartupContracts();
        });
    }

    React.useEffect(() => {
        if (vars.startupId) {
            Promise.all([
                getStartupDetails(vars.startupId, false),
                getStartupRankingApplication(),
                getStartupContracts(),
                getStartupTermByType('ranking2021co'),
            ]).then(res => {
                addVars({
                    startupDetails: res[0],
                });
            });
        }
        // eslint-disable-next-line
    }, [vars.startupId]);

    useEffectOnce(() => {
        // Validate investiment if avaliable
        if (vars.validate) {
            emitAlert({
                header: t('investmentValidated.title', { context: vars.validate.status }),
                message: t('investmentValidated.message', { context: vars.validate.status }),
            });
        }

        // Initialize the module's service
        getRanking().finally(() => {
            Promise.all([getStartups()]).then(res => setReady(true));
        });
    });

    return {
        // Ready to load?
        ready,
        // Variables handlers
        addVars,
        setVars,
        vars,
        // Include vars functions
        updateSteps,
        // Custom Functions
        calculateContractPoints,
        setReturnTo,
        // Custom functions / Application
        getStartupRankingApplication,
        putStartupRankingApplication,
        // Custom functions / Contracts
        getCompanyByDomain,
        getStartupContracts,
        postStartupContract,
        postStartupContractValidationEmail,
        postStartupContractReconsideration,
        deleteStartupContract,
        // Custom functions / Startups
        getStartups,
        getStartupDetails,
        putStartupDetails,
        // Custom functions / Term
        getTerm,
        getStartupTermByType,
        postStartupTerm,
    };
}
export default useRankingStartup;
